"use client";

import * as React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const planningItems = [{
  title: "Review the current layout",
  description: "Look at the shower, tub, toilet, vanity, door swing, storage, and walking space before deciding what needs to change."
}, {
  title: "Choose the project scope",
  description: "Decide if you want a full bathroom remodel or a smaller update like a shower remodel, tub replacement, vanity update, flooring update, or fixture change."
}, {
  title: "Think about materials",
  description: "Tile, flooring, vanities, countertops, lighting, mirrors, faucets, and shower fixtures can all affect the look, function, and quote."
}, {
  title: "Check cost and permit factors",
  description: "Bathroom remodel pricing, timelines, and permit needs can vary based on plumbing, electrical work, layout changes, materials, and project size."
}];
export default function BathroomRemodelInsights() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="bathroomremodelinsights" className="relative w-full overflow-hidden bg-background py-24 md:py-32">
      <div className="pointer-events-none absolute top-8 left-8 hidden font-mono text-[10px] tracking-widest text-muted-foreground/40 md:block">
        LAT. 28.0395° N // LONG. 81.9498° W
      </div>

      <div className="pointer-events-none absolute right-8 bottom-8 hidden font-mono text-[10px] tracking-widest text-muted-foreground/40 md:block">
        SEC. 04 // REV. A // LAKELAND
      </div>

      <div className="pointer-events-none absolute top-12 right-12 hidden h-4 w-4 border-t border-r border-muted-foreground/20 md:block" />
      <div className="pointer-events-none absolute bottom-12 left-12 hidden h-4 w-4 border-b border-l border-muted-foreground/20 md:block" />

      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <motion.div ref={textRef} initial={{
        opacity: 0,
        y: 30
      }} animate={isTextVisible ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-5">
            <span className="mb-6 block font-mono text-xs font-semibold tracking-widest text-muted-foreground uppercase">
              Bathroom Remodel Planning
            </span>

            <h2 className="relative mb-8 font-default text-3xl leading-tight font-bold text-foreground md:text-4xl lg:text-5xl">
              Bathroom Remodel Cost, Timeline, and Permits in Lakeland, FL
            </h2>

            <p className="text-base leading-relaxed text-muted-foreground md:text-lg">
              Bathroom remodel projects in Lakeland, FL can vary depending on
              the current bathroom layout, shower or tub setup, tile, flooring,
              vanity size, storage needs, plumbing, electrical work, and the
              updates you want to make.
            </p>
          </div>

          <div className="lg:col-span-7">
            <p className="text-base leading-relaxed text-muted-foreground md:text-lg mb-8">
              Before requesting a quote, it helps to know whether you are
              planning a full bathroom remodel, shower update, tub replacement,
              vanity update, flooring update, fixture change, or layout change.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {planningItems.map((item, i) => <div key={item.title} data-index={i} className="rounded-lg border border-border bg-card p-5">
                  <div className="font-mono text-xs tracking-[0.18em] uppercase text-primary mb-3">
                    0{i + 1}
                  </div>

                  <h3 className="font-default text-lg font-semibold text-foreground mb-2">
                    {item.title}
                  </h3>

                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </div>)}
            </div>

            <div className="mt-8 flex flex-wrap gap-5">
              <Link to="/bathroom-remodel-cost-in-lakeland-fl/" className="group inline-flex items-center gap-2 text-primary underline underline-offset-4 hover:text-primary/80 transition-colors">
                Review bathroom remodel cost factors
                <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>

              <Link to="/contact/#request-your-bathroom-remodel-quote" className="group inline-flex items-center gap-2 text-primary underline underline-offset-4 hover:text-primary/80 transition-colors">
                Request a free bathroom remodel quote
                <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}